import { z } from 'zod';
import { emptySkills, skillsSchema } from './skills.ts';

/**
 * Per-user memory: what fuse knows about the candidate. One document per user. Scalar facts live in a flat `fields` map
 * so every value carries provenance; lists (facts, saved words, practiced questions, tabs) are capped arrays.
 *
 * Write precedence (see docs/decisions/ADR-0007): user/form edits beat live-tool writes, which beat the post-call
 * updater. The updater only fills empty fields. A lower-precedence writer never overwrites a higher one.
 */
export const MEMORY_SOURCES = ['updater', 'tool', 'form', 'user'] as const;
export type MemorySource = (typeof MEMORY_SOURCES)[number];
const PRECEDENCE: Record<MemorySource, number> = { updater: 1, tool: 2, form: 3, user: 3 };

// Scalar facts fuse can learn. `voice: false` fields are form-only (never written by the model): consent and phone
// numbers given by voice are unreliable and not valid consent.
export const MEMORY_FIELDS = {
  'profile.name': { label: 'Name', voice: true, max: 80 },
  'profile.background': { label: 'Background', voice: true, max: 400 },
  'profile.routine': { label: 'Daily routine', voice: true, max: 300 },
  'profile.motherTongue': { label: 'Mother tongue', voice: true, max: 40 },
  'profile.timezone': { label: 'Timezone', voice: false, max: 60 },
  // Keys kept from the first memory model (no migration); the labels are what the model and the user see.
  'goal.broad': { label: 'Career goal', voice: true, max: 300 },
  'goal.painPoint': { label: 'Biggest worry', voice: true, max: 300 },
  'interview.role': { label: 'Target role', voice: true, max: 100 },
  'interview.company': { label: 'Company', voice: true, max: 100 },
  'interview.date': { label: 'Interview date', voice: true, max: 40 },
  'interview.round': { label: 'Round', voice: true, max: 100 },
  'interview.jdSummary': { label: 'Job description', voice: false, max: 1000 },
  'prefs.nudgeChannel': { label: 'Nudges via', voice: false, max: 20 },
  // Not by voice while no screen shows it (reminders are hidden): Profile must show everything the coach saves.
  'prefs.bestTime': { label: 'Best time to nudge', voice: false, max: 60 },
  'prefs.phone': { label: 'Phone', voice: false, max: 20 },
  'prefs.whatsappConsentAt': { label: 'WhatsApp consent', voice: false, max: 40 },
} as const satisfies Record<string, { label: string; voice: boolean; max: number }>;
export type MemoryField = keyof typeof MEMORY_FIELDS;
export const MEMORY_FIELD_KEYS = Object.keys(MEMORY_FIELDS) as MemoryField[];
export const VOICE_FIELD_KEYS = MEMORY_FIELD_KEYS.filter((k) => MEMORY_FIELDS[k].voice);

export const JOURNEY_STAGES = ['new', 'call1_done', 'context_ready', 'assessed'] as const;
export type JourneyStage = (typeof JOURNEY_STAGES)[number];

export const LIMITS = { facts: 50, savedWords: 200, practiced: 30 } as const;

// One kind of speaking task in the assessment set (ADR-0012): answered (scored or not) or skipped, and when. Every
// field reads leniently: a rollback must still load an entry a newer build wrote. A status this build doesn't know
// counts as present (not left to do) and is written back as it was; one that isn't even a string reads as 'unknown'.
const assessmentItem = z.looseObject({
  status: z.string().catch('unknown'),
  sessionId: z.string().catch(''),
  at: z.string().catch(''),
  scored: z.boolean().catch(false),
});

// The skills at a moment (the baseline, a check-in's result), read leniently like the rest of memory.
const skillsSnapshot = z.looseObject({
  cefr: z.string().nullable().catch(null),
  overall: z.number().nullable().catch(null),
  pronunciation: z.number().nullable().catch(null),
  fluency: z.number().nullable().catch(null),
  grammar: z.number().nullable().catch(null),
  vocabulary: z.number().nullable().catch(null),
  at: z.string().catch(''),
});

const passageSchema = z.looseObject({
  text: z.string(),
  source: z.string().catch('fallback'),
  at: z.string().catch(''),
});

// One check-in (flow 002, src/shared/rounds.ts): its own record of the set, its passage, and its result once finished.
const checkInRound = z.looseObject({
  n: z.number().int().min(1).catch(1),
  startedAt: z.string().catch(''),
  items: z.record(z.string(), assessmentItem).default({}).catch({}),
  passage: passageSchema.optional().catch(undefined),
  // Calls of this round that opened an exercise, and all of them (the call-2 caps, per round).
  tries: z.number().int().min(0).optional().catch(undefined),
  calls: z.number().int().min(0).optional().catch(undefined),
  // "Not today": a call of this round that did none of it. It's offered again a few days later.
  declinedAt: z.string().optional().catch(undefined),
  finishedAt: z.string().optional().catch(undefined),
  // The call that finished it: a late answer from that call can still count, and refreshes the result.
  finishedIn: z.string().optional().catch(undefined),
  // This round's own measurement: its scored answers alone, so its result isn't weeks of practice averaged in.
  skills: skillsSchema.optional().catch(undefined),
  result: skillsSnapshot.optional().catch(undefined),
});

// Unknown sources (from a newer build) read as the lowest precedence rather than failing the whole document.
const tracked = z.object({ value: z.string(), source: z.enum(MEMORY_SOURCES).catch('updater'), at: z.string() });
export type Tracked = z.infer<typeof tracked>;

// Lenient on read so a rollback can still load memory written by a newer build: unknown field keys and top-level
// keys are kept (and written back untouched), an unknown stage falls back to 'new'.
export const userMemory = z.looseObject({
  schema: z.literal(1).default(1),
  version: z.number().int().nonnegative().default(0),
  fields: z.record(z.string(), tracked).default({}) as unknown as z.ZodDefault<
    z.ZodType<Partial<Record<MemoryField, Tracked>>>
  >,
  resume: z
    .object({
      summary: z.string(),
      roles: z.array(z.string()).max(20),
      years: z.number().nullable(),
      skills: z.array(z.string()).max(40),
      projects: z.array(z.string()).max(20),
      at: z.string(),
    })
    .optional(),
  facts: z
    .array(
      z.object({
        text: z.string().max(300),
        quote: z.string().max(300).optional(),
        sessionId: z.string(),
        at: z.string(),
      }),
    )
    .default([]),
  savedWords: z
    .array(z.object({ word: z.string().max(60), meaning: z.string().max(200).optional(), at: z.string() }))
    .default([]),
  practiced: z
    .array(
      z.object({
        question: z.string().max(300),
        sessionId: z.string(),
        at: z.string(),
        overall: z.number().nullable(),
        cefr: z.string().nullable(),
      }),
    )
    .default([]),
  // Loose entries: a newer build may add keys (e.g. hidden) that a rollback write must keep.
  tabs: z.array(z.looseObject({ type: z.string(), openedAt: z.string(), reason: z.string() })).default([]),
  journey: z
    .object({ stage: z.enum(JOURNEY_STAGES).catch('new'), at: z.string() })
    .default({ stage: 'new', at: new Date(0).toISOString() }),
  completedSessions: z.array(z.string()).default([]), // post-call updater ran for these (idempotency)
  skills: skillsSchema.default(emptySkills), // running picture across scored answers (src/shared/skills.ts)
  // Keyed by task kind; a key this build doesn't know is kept (loose), not dropped. See src/shared/assessment.ts.
  assessment: z
    .looseObject({
      items: z.record(z.string(), assessmentItem).default({}),
      // Call 2's read-aloud script, written once when setup finishes (src/shared/passage.ts).
      passage: passageSchema.optional().catch(undefined),
      // Call-2 calls that opened an exercise, and all call-2 conversations (see CALL2_TRIES and CALL2_CALLS).
      call2Tries: z.number().int().min(0).optional().catch(undefined),
      call2Calls: z.number().int().min(0).optional().catch(undefined),
      // The call whose completion saved the baseline (or found nothing scored to save): a late answer from that call
      // refreshes it.
      baselineIn: z.string().optional().catch(undefined),
      // Where they started, saved when the journey moves to assessed, so progress can be read against it.
      baseline: skillsSnapshot.optional().catch(undefined),
      // Check-ins after that (flow 002): the set again, about every two weeks. The baseline stays the start.
      // Each round reads on its own: one a build can't read is dropped, not every round with it.
      rounds: z
        .array(z.unknown())
        .transform((list) =>
          list.flatMap((r) => {
            const round = checkInRound.safeParse(r);
            return round.success ? [round.data] : [];
          }),
        )
        .optional()
        .catch(undefined),
    })
    .default({ items: {} }),
});
export type UserMemory = z.infer<typeof userMemory>;

export function emptyMemory(): UserMemory {
  return userMemory.parse({});
}

const EMAIL = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
const DIGIT_RUN = /\+?\(?\d[\d\s().-]{8,}\d/g;
const YEAR = /^(19|20)\d{2}$/;

// A phone number is 10-15 digits. Dates, times and year ranges ("2026-10-15 10", "2016-2019) (2019-2023") can reach
// 10 digits too; they look like two years, or a year with two or more 1-2 digit groups (month, day, hour). A number
// may end in "2019" (+91 987 654 2019) without looking like either.
function isPhone(run: string): boolean {
  const n = run.replace(/\D/g, '').length;
  if (n < 10 || n > 15) return false;
  const groups = run.split(/\D+/).filter(Boolean);
  const years = groups.filter((g) => YEAR.test(g)).length;
  const small = groups.filter((g) => g.length <= 2).length;
  const dateLike = years >= 2 || (years === 1 && small >= 2);
  return !dateLike;
}

/** True when text carries an email or a phone number. Contact details only ever come from forms. */
export function hasContactDetails(text: string): boolean {
  return new RegExp(EMAIL.source).test(text) || (text.match(DIGIT_RUN) ?? []).some(isPhone);
}

/** Removes emails and phone numbers (e.g. from what an LLM read off a resume). */
export function stripContactDetails(text: string): string {
  if (!hasContactDetails(text)) return text;
  return text
    .replace(EMAIL, '')
    .replace(DIGIT_RUN, (m) => (isPhone(m) ? '' : m))
    .replace(/\(\s*[,;]?\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export type FieldWrite = { field: MemoryField; value: string; source: MemorySource; at?: string };
export type WriteResult = { applied: boolean; reason?: string };

/** Applies one scalar write under the precedence rules. Pure: returns a new memory. */
export function writeField(memory: UserMemory, w: FieldWrite): { memory: UserMemory; result: WriteResult } {
  const spec = MEMORY_FIELDS[w.field];
  const value = w.value.trim().slice(0, spec.max);
  if (!value) return { memory, result: { applied: false, reason: 'empty value' } };
  if ((w.source === 'tool' || w.source === 'updater') && !spec.voice) {
    return { memory, result: { applied: false, reason: `${w.field} is form-only` } };
  }
  if ((w.source === 'tool' || w.source === 'updater') && hasContactDetails(value)) {
    return { memory, result: { applied: false, reason: 'contact details are only taken from forms' } };
  }
  const existing = memory.fields[w.field];
  // A tombstone (empty value) left by the user clearing a field: nothing inferred may refill it; saying it again in a
  // call (tool) or typing it (form/user) may.
  if (existing && !existing.value && w.source === 'updater') {
    return { memory, result: { applied: false, reason: 'cleared by the user' } };
  }
  if (existing?.value) {
    if (w.source === 'updater')
      return { memory, result: { applied: false, reason: 'updater only fills empty fields' } };
    if (PRECEDENCE[w.source] < PRECEDENCE[existing.source]) {
      return { memory, result: { applied: false, reason: `kept ${existing.source} value` } };
    }
  }
  const at = w.at ?? new Date().toISOString();
  return {
    memory: { ...memory, fields: { ...memory.fields, [w.field]: { value, source: w.source, at } } },
    result: { applied: true },
  };
}

/** The user cleared a value (e.g. a misheard name). Leaves a tombstone so the post-call updater won't refill it. */
export function clearField(memory: UserMemory, field: MemoryField, at = new Date().toISOString()): UserMemory {
  return { ...memory, fields: { ...memory.fields, [field]: { value: '', source: 'user', at } } };
}

/** Fields owned by a setup form (validated there), so the Profile tab's free-text edit can't write them. */
export const FORM_MANAGED_FIELDS: ReadonlySet<MemoryField> = new Set([
  'prefs.nudgeChannel',
  'prefs.phone',
  'prefs.whatsappConsentAt',
  'interview.jdSummary',
]);

/** Form-only personal data never shown to an LLM (prompts, updater context). */
export const PRIVATE_FIELDS: ReadonlySet<MemoryField> = new Set(['prefs.phone', 'prefs.whatsappConsentAt']);

export function addFact(memory: UserMemory, fact: UserMemory['facts'][number]): UserMemory {
  if (memory.facts.some((f) => f.text.toLowerCase() === fact.text.toLowerCase())) return memory;
  return { ...memory, facts: [...memory.facts, fact].slice(-LIMITS.facts) };
}

export function saveWord(memory: UserMemory, word: UserMemory['savedWords'][number]): UserMemory {
  const rest = memory.savedWords.filter((w) => w.word.toLowerCase() !== word.word.toLowerCase());
  return { ...memory, savedWords: [...rest, word].slice(-LIMITS.savedWords) };
}

export function addPracticed(memory: UserMemory, entry: UserMemory['practiced'][number]): UserMemory {
  return { ...memory, practiced: [...memory.practiced, entry].slice(-LIMITS.practiced) };
}

export function setResume(memory: UserMemory, resume: Omit<NonNullable<UserMemory['resume']>, 'at'>): UserMemory {
  return { ...memory, resume: { ...resume, at: new Date().toISOString() } };
}

export function removeResume(memory: UserMemory): UserMemory {
  const { resume: _removed, ...rest } = memory;
  // Read-aloud passages written from the resume go with it; call 2 and check-ins then use the fixed ones.
  const fromResume = (p?: { source: string }) => p?.source === 'resume';
  const { passage, rounds, ...assessment } = rest.assessment;
  return {
    ...rest,
    assessment: {
      ...assessment,
      ...(passage && !fromResume(passage) ? { passage } : {}),
      ...(rounds
        ? { rounds: rounds.map(({ passage: p, ...r }) => (p && !fromResume(p) ? { ...r, passage: p } : r)) }
        : {}),
    },
  };
}

export function fieldValue(memory: UserMemory, field: MemoryField): string | undefined {
  return memory.fields[field]?.value || undefined;
}

/** Stage moves only forward and only from the expected stage (conditional transition). */
export function advanceStage(memory: UserMemory, from: JourneyStage, to: JourneyStage): UserMemory {
  if (memory.journey.stage !== from) return memory;
  return { ...memory, journey: { stage: to, at: new Date().toISOString() } };
}
